import React, { type CSSProperties } from 'react'
import { FormattedMessage } from 'react-intl'
import { useCopyToClipboard } from 'react-use'
import { intl } from 'lang/intl'

import { TtIcon as Icon, TtIconButton } from '@23/ui-react'

import { useWebinarColors } from '../../../../hooks/useWebinarColors'
import TooltipComponent from '../../../../components/TooltipComponent/TooltipComponent'

import './QuestionComponent.scss'

type Props = {
  label: string
  by: string
  isUnread?: boolean
  isInactive?: boolean
  isPersistingViewButton?: boolean
  onAnsweredClick?: () => void
  onDismissClick?: () => void
  onViewClick?: () => void
}

const QuestionComponent = ({
  label,
  by,
  isUnread = false,
  isInactive = false,
  isPersistingViewButton = false,
  onAnsweredClick,
  onDismissClick,
  onViewClick,
}: Props) => {
  const colors = useWebinarColors()
  const [, copyToClipboard] = useCopyToClipboard()
  const style = {
    '--question-tab-bg-color': colors.tabs,
    '--question-tab-border-color': isUnread ? colors.accent : colors.tabs,
    '--question-tab-opacity': isInactive ? 0.5 : 1,
    '--question-tab-answer-button-bg-color': colors.accent.transparent('16%'),
    '--question-tab-answer-button-hover-bg-color':
      colors.accent.transparent('32%'),
    '--question-tab-dismiss-button-bg-color':
      colors.tabs.contrast.transparent('0%'),
    '--question-tab-dismiss-button-hover-bg-color':
      colors.tabs.contrast.transparent('8%'),
    '--question-tab-dismiss-button-border-color':
      colors.sidebar.contrast.transparent('8%'),
  } as CSSProperties

  return (
    <div className="question-component" style={style}>
      <div className="question-content">
        <div className="question-info">
          <p
            className="question-by"
            style={{ color: colors.tabs.contrast.color }}
          >
            {by}
          </p>
          <p
            className="question-label"
            style={{ color: colors.tabs.contrast.color }}
          >
            {label}
          </p>
        </div>
        {(onAnsweredClick || onDismissClick) && (
          <div className="question-functions">
            {onAnsweredClick && (
              <button
                className="question-mark-answered-button"
                type="button"
                onClick={onAnsweredClick}
              >
                <Icon name="check" library="23" />
                <FormattedMessage defaultMessage="Answered" />
              </button>
            )}
            {onDismissClick && (
              <button
                className="question-dismiss-button"
                type="button"
                onClick={onDismissClick}
                style={{
                  backgroundColor: colors.tabs.highContrast.transparent('5%'),
                }}
              >
                <Icon name="close" library="23" />
                <FormattedMessage defaultMessage="Dismiss" />
              </button>
            )}
          </div>
        )}
      </div>
      <TooltipComponent
        wrapperClassName="question-copy-tooltip"
        placement="topRight"
        title={intl.formatMessage({ defaultMessage: 'Copy question' })}
      >
        <TtIconButton
          label={intl.formatMessage({ defaultMessage: 'Copy question' })}
          name="copy"
          className="question-copy-button"
          onClick={() => {
            copyToClipboard(`${by} — ${label}`)
          }}
        />
      </TooltipComponent>
      {onViewClick && (
        <TooltipComponent
          placement="topRight"
          title={
            isPersistingViewButton
              ? intl.formatMessage({ defaultMessage: 'Hide Question Card' })
              : intl.formatMessage({ defaultMessage: 'Show Question Card' })
          }
        >
          <Icon
            name="views"
            className="question-show"
            onClick={onViewClick}
            data-is-persisted={isPersistingViewButton}
          />
        </TooltipComponent>
      )}
    </div>
  )
}

export default QuestionComponent
